import type { EntregaFormState } from './EntregaFormState.ts';
import { entregaMapMarkersInView } from './entregaMapModel.ts';

export const ENTREGA_MAP_TILE_ZOOM = 15;
export const OSM_TILE_SIZE = 256;

export type MapTileCoord = { x: number; y: number; z: number };

export function latLngToTile(lat: number, lng: number, zoom: number): MapTileCoord {
  const n = 2 ** zoom;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y, z: zoom };
}

export function tileKey(tile: MapTileCoord): string {
  return `${tile.z}/${tile.x}/${tile.y}`;
}

export function osmTileUrl(tile: MapTileCoord): string {
  return `https://tile.openstreetmap.org/${tile.z}/${tile.x}/${tile.y}.png`;
}

export function entregaMapTileCacheKey(state: EntregaFormState): string | null {
  let { markers } = entregaMapMarkersInView(state);
  if (markers.length === 0 && state.puntoLat != null && state.puntoLng != null) {
    markers = [{ lat: state.puntoLat, lng: state.puntoLng, label: 'Punto', color: '' }];
  }
  if (markers.length === 0) return null;
  const parts = markers.map((m) => `${m.label}:${m.lat.toFixed(5)},${m.lng.toFixed(5)}`);
  return parts.join('|');
}

export function tilesForEntregaMapState(
  state: EntregaFormState,
  zoom = ENTREGA_MAP_TILE_ZOOM,
  maxTiles = 9,
): MapTileCoord[] {
  let { markers } = entregaMapMarkersInView(state);
  if (markers.length === 0 && state.puntoLat != null && state.puntoLng != null) {
    markers = [{ lat: state.puntoLat, lng: state.puntoLng, label: 'Punto', color: '' }];
  }
  if (markers.length === 0) return [];
  const lats = markers.map((m) => m.lat);
  const lngs = markers.map((m) => m.lng);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);
  const padLat = Math.max((maxLat - minLat) * 0.25, 0.012);
  const padLng = Math.max((maxLng - minLng) * 0.25, 0.012);
  minLat -= padLat;
  maxLat += padLat;
  minLng -= padLng;
  maxLng += padLng;
  const nw = latLngToTile(maxLat, minLng, zoom);
  const se = latLngToTile(minLat, maxLng, zoom);
  const tiles: MapTileCoord[] = [];
  for (let x = nw.x; x <= se.x; x++) {
    for (let y = nw.y; y <= se.y; y++) {
      tiles.push({ x, y, z: zoom });
    }
  }
  return tiles.slice(0, maxTiles);
}

/** Minimal form state for prefetching OSM tiles at a delivery point. */
export function entregaFormStateForPunto(punto: {
  comunaPublicId: string;
  publicId: string;
  direccion: string;
  lat: number;
  lng: number;
}): EntregaFormState {
  return {
    comunaPublicId: punto.comunaPublicId,
    puntoEntregaPublicId: punto.publicId,
    direccion: punto.direccion,
    fecha: '',
    itemPublicId: '',
    cicloPublicId: '',
    unidadPrograma: '',
    nutricionistaPublicIds: [],
    puntoLat: punto.lat,
    puntoLng: punto.lng,
    gpsLat: null,
    gpsLng: null,
  };
}
